import { useId, type SVGProps } from "react";

/**
 * Minimal lucide-style stroke icons for glyphs Astryx has no semantic name for.
 * Each matches Astryx's IconType (ComponentType<SVGProps<SVGSVGElement>>).
 */

function base(props: SVGProps<SVGSVGElement>) {
  return {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    width: "1em",
    height: "1em",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    ...props,
  } as const;
}

export function SearchIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(props)}>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  );
}

export function PlusIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(props)}>
      <path d="M5 12h14" />
      <path d="M12 5v14" />
    </svg>
  );
}

export function PinIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(props)}>
      <path d="M12 17v5" />
      <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1z" />
    </svg>
  );
}

export function LogoutIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(props)}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  );
}

export function TrashIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(props)}>
      <path d="M3 6h18" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

export function ExternalIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(props)}>
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </svg>
  );
}

export function CheckIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(props)}>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

export function CalendarIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(props)}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4" />
      <path d="M8 2v4" />
      <path d="M3 10h18" />
    </svg>
  );
}

export function InboxIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(props)}>
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  );
}

export function SparkleIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(props)}>
      <path d="M12 3v0c.7 3.9 1.9 5.2 6 6-4.1.8-5.3 2.1-6 6-.7-3.9-1.9-5.2-6-6 4.1-.8 5.3-2.1 6-6z" />
      <path d="M19 14c.4 2.1 1 2.7 3 3-2 .3-2.6.9-3 3-.4-2.1-1-2.7-3-3 2-.3 2.6-.9 3-3z" />
      <path d="M5 16c.3 1.6.8 2.1 2.5 2.5-1.7.4-2.2.9-2.5 2.5-.3-1.6-.8-2.1-2.5-2.5 1.7-.4 2.2-.9 2.5-2.5z" />
    </svg>
  );
}

export function GlobeIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(props)}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
      <path d="M2 12h20" />
    </svg>
  );
}

export function PowerIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(props)}>
      <path d="M12 3v9" />
      <path d="M18.4 6.6a9 9 0 1 1-12.8 0" />
    </svg>
  );
}

export function DownloadIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(props)}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="m7 10 5 5 5-5" />
      <path d="M12 15V3" />
    </svg>
  );
}

export function KeyIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(props)}>
      <path d="m21 2-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0 3 3L22 7l-3-3m-3.5 3.5L19 4" />
    </svg>
  );
}

export function WalletIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(props)}>
      <path d="M19 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0 0 4h15a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5" />
      <path d="M17 13h.01" />
    </svg>
  );
}

export function GripIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base(props)}>
      <circle cx="9" cy="6" r="1" />
      <circle cx="9" cy="12" r="1" />
      <circle cx="9" cy="18" r="1" />
      <circle cx="15" cy="6" r="1" />
      <circle cx="15" cy="12" r="1" />
      <circle cx="15" cy="18" r="1" />
    </svg>
  );
}

/**
 * The brand mark: a glowing core inside an orbiting arc — hq as a personal
 * command center. The orbit turns slowly; the core breathes. Both stop under
 * prefers-reduced-motion. Gradient ids are per-instance so several marks can
 * coexist on one page.
 */
export function HqMark(props: SVGProps<SVGSVGElement>) {
  const uid = useId().replace(/:/g, "");
  const orbit = `hq-orbit-${uid}`;
  const core = `hq-core-${uid}`;
  const glow = `hq-glow-${uid}`;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width="1.35em"
      height="1.35em"
      role="img"
      aria-label="hq"
      {...props}
    >
      <defs>
        <linearGradient id={orbit} x1="3" y1="3" x2="21" y2="21" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#ff9ec6" />
          <stop offset="0.55" stopColor="#ff6fa5" />
          <stop offset="1" stopColor="#8d2f57" />
        </linearGradient>
        <radialGradient id={core} cx="0.35" cy="0.3" r="0.85">
          <stop offset="0" stopColor="#ffd7e7" />
          <stop offset="0.45" stopColor="#ff6fa5" />
          <stop offset="1" stopColor="#d94f82" />
        </radialGradient>
        <filter id={glow} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="1.6" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <style>{`
        @keyframes hq-spin { to { transform: rotate(360deg); } }
        @keyframes hq-breathe { 50% { opacity: 0.82; transform: scale(0.94); } }
        .hq-orbit-${uid} { transform-origin: 12px 12px; animation: hq-spin 14s linear infinite; }
        .hq-core-${uid} { transform-origin: 12px 12px; animation: hq-breathe 3.2s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .hq-orbit-${uid}, .hq-core-${uid} { animation: none; }
        }
      `}</style>
      <g className={`hq-orbit-${uid}`}>
        {/* Circumference ≈ 53.4. Rotated -90° with a dash offset so the arc
            starts just past the satellite and the gaps fall on the sides. */}
        <circle
          cx="12"
          cy="12"
          r="8.5"
          fill="none"
          stroke={`url(#${orbit})`}
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeDasharray="35.5 5.2 6.6 6.1"
          strokeDashoffset="-3"
          transform="rotate(-90 12 12)"
        />
        <circle cx="12" cy="3.5" r="1.7" fill="#ffd7e7" />
      </g>
      <circle
        className={`hq-core-${uid}`}
        cx="12"
        cy="12"
        r="4.1"
        fill={`url(#${core})`}
        filter={`url(#${glow})`}
      />
    </svg>
  );
}
